import { thousandsSeparator } from '@utils/thousandsSeparator';
import { capitalizeOnlyFirstWord } from '@utils/capitalize';
import {
  reviewsFrequentlyMentionedGET, reviewsGET, reviewsPhotosGET,
} from '@services/ReviewService';
import parseStatisticsData from './parsers/parseStatisticsData';
import {
  SET_REVIEWS,
  SET_REVIEWS_PAGE_NUMBER,
  SET_REVIEWS_TOTAL_RESULTS,
  SET_REVIEWS_CURRENT_RESULTS,
  SET_REVIEWS_LOADING_STATE,
  SET_REVIEWS_STAR_FILTERING,
  SET_REVIEWS_SORT_BY,
  SET_REVIEWS_FREQUENTLY_MENTIONED,
  SET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED,
  SET_REVIEWS_STATISTICS,
  SET_REVIEWS_PHOTOS,
  SET_REVIEWS_SEARCH_QUERY,
  UPDATE_HEADER,
  UPDATE_IS_FILTERED,
  SET_IS_SUBMITTING_REVIEW,
  SET_SHOW_SUBMITTING_ERROR,
  SET_IS_CURRENT_PRODUCT_REVIEWED,
  SET_IS_CONDENSED,
} from '../../types/setters';
import {
  GET_REVIEWS,
  GET_REVIEWS_LOADING_STATE,
  GET_REVIEWS_PAGE_NUMBER,
  GET_REVIEWS_TOTAL_RESULTS,
  GET_REVIEWS_OVERALL_PAGES,
  GET_REVIEWS_STAR_FILTERING,
  GET_REVIEWS_SORT_BY,
  GET_REVIEWS_FREQUENTLY_MENTIONED,
  GET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED,
  GET_REVIEWS_STATISTICS,
} from '../../types/getters';
import { FETCH_REVIEWS_PHOTOS, RESET_FILTERS } from '../../types/actions';
import {
  SORT_BY_OPTION, ALL_STAR_RATINGS, INITIAL_PAGE_LIMIT, SUBSEQUENT_PAGE_LIMIT, ONE_STAR_RATING, LOADING_STATE,
} from '../../constants/reviews';

/**
 * @returns {ReviewsState}
 */
export const state = () => ({
  page: 1,
  totalResults: null,
  currentResults: null,
  reviews: [],
  starFiltering: ALL_STAR_RATINGS,
  sortBy: SORT_BY_OPTION.NEWEST,
  searchQuery: '',
  loadingState: LOADING_STATE.LOADED, // initial reviews are loaded from the product API response
  frequentlyMentioned: [],
  selectedFrequentlyMentioned: null,
  statistics: null,
  photos: [],
  reviewsHeader: '',
  isFiltered: false,
  isSubmittingReview: false,
  showSubmittingError: false,
  isCurrentProductReviewed: false,
  isReviewsSectionCondensed: true,
});

export const mutations = {
  /**
   * @param {ReviewsState} reviewsState
   * @param {Review[]} reviews
   */
  [SET_REVIEWS](reviewsState, reviews) {
    reviewsState.reviews = reviews;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @param {string} loadingState
   */
  [SET_REVIEWS_LOADING_STATE](reviewsState, loadingState) {
    reviewsState.loadingState = loadingState;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @param {number} page
   */
  [SET_REVIEWS_PAGE_NUMBER](reviewsState, page) {
    reviewsState.page = page;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @param {number} stars
   */
  [SET_REVIEWS_STAR_FILTERING](reviewsState, stars) {
    reviewsState.starFiltering = stars;
  },
  /**
   * @param {ReviewsState} reviewState
   * @param {string} sortBy
   */
  [SET_REVIEWS_SORT_BY](reviewState, sortBy) {
    reviewState.sortBy = sortBy;
  },
  /**
   * @param {ReviewsState} reviewState
   * @param {number} totalResults
   */
  [SET_REVIEWS_TOTAL_RESULTS](reviewState, totalResults) {
    reviewState.totalResults = totalResults;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {number} currentResults
   */
  [SET_REVIEWS_CURRENT_RESULTS](reviewState, currentResults) {
    reviewState.currentResults = currentResults;
  },
  /**
   * @param {ReviewsState} reviewState
   * @param {string[]} frequentlyMentioned
   */
  [SET_REVIEWS_FREQUENTLY_MENTIONED](reviewState, frequentlyMentioned) {
    reviewState.frequentlyMentioned = frequentlyMentioned;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {string | null} selectedFrequentlyMentioned
   */
  [SET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED](reviewState, selectedFrequentlyMentioned) {
    reviewState.selectedFrequentlyMentioned = selectedFrequentlyMentioned;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {ReviewStatisticsObject | null} statisticsObject
   */
  [SET_REVIEWS_STATISTICS](reviewState, statisticsObject) {
    reviewState.statistics = statisticsObject;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {ReviewPhotosObject[]} photoObjects
   */
  [SET_REVIEWS_PHOTOS](reviewState, photoObjects) {
    reviewState.photos = photoObjects;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {string} searchQuery
   */
  [SET_REVIEWS_SEARCH_QUERY](reviewState, searchQuery) {
    reviewState.searchQuery = searchQuery;
  },

  /**
   * @param {ReviewsState} reviewState
   */
  [UPDATE_HEADER](reviewState) {
    const {
      currentResults, selectedFrequentlyMentioned, starFiltering, searchQuery,
    } = reviewState;
    let result = `${currentResults ? thousandsSeparator(currentResults) : 'No' } review`;
    result += currentResults === 1 ? '' : 's';

    if (selectedFrequentlyMentioned) {
      result += ` with "${capitalizeOnlyFirstWord(selectedFrequentlyMentioned)}"`;
    } else {
      if (starFiltering !== ALL_STAR_RATINGS) {
        result += ` with ${starFiltering} ${starFiltering === ONE_STAR_RATING ? 'star' : 'stars'}`;
      }

      if (searchQuery) {
        result += ` mentioning "${searchQuery}"`;
      }
    }

    reviewState.reviewsHeader = result;
  },

  /**
   * @param {ReviewsState} reviewState
   */
  [UPDATE_IS_FILTERED](reviewState) {
    const { selectedFrequentlyMentioned, starFiltering, searchQuery } = reviewState;
    let result = false;
    if (selectedFrequentlyMentioned) result = true;
    if (starFiltering !== ALL_STAR_RATINGS) result = true;
    if (searchQuery) result = true;
    reviewState.isFiltered = result;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {boolean} value
   */
  [SET_IS_SUBMITTING_REVIEW](reviewState, value) {
    reviewState.isSubmittingReview = value;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {boolean} value
   */
  [SET_SHOW_SUBMITTING_ERROR](reviewState, value) {
    reviewState.showSubmittingError = value;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {boolean} value
   */
  [SET_IS_CURRENT_PRODUCT_REVIEWED](reviewState, value) {
    reviewState.isCurrentProductReviewed = value;
  },

  /**
   * @param {ReviewsState} reviewState
   * @param {boolean} value
   */
  [SET_IS_CONDENSED](reviewState, value) {
    reviewState.isReviewsSectionCondensed = value;
  },
};

export const getters = {
  /**
   * @param {ReviewsState} reviewsState
   * @returns {Review[]}
   */
  [GET_REVIEWS](reviewsState) {
    return reviewsState.reviews;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {string}
   */
  [GET_REVIEWS_LOADING_STATE](reviewsState) {
    return reviewsState.loadingState;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {number}
   */
  [GET_REVIEWS_PAGE_NUMBER](reviewsState) {
    return reviewsState.page;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {number | 'ALL_STAR_RATINGS'}
   */
  [GET_REVIEWS_STAR_FILTERING](reviewsState) {
    return reviewsState.starFiltering;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {ReviewStatisticsObject | null}
   */
  [GET_REVIEWS_STATISTICS](reviewsState) {
    return reviewsState.statistics;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {string}
   */
  [GET_REVIEWS_SORT_BY](reviewsState) {
    return reviewsState.sortBy;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {number | null}
   */
  [GET_REVIEWS_TOTAL_RESULTS](reviewsState) {
    return reviewsState.totalResults;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {number}
   */
  [GET_REVIEWS_OVERALL_PAGES](reviewsState) {
    const { currentResults } = reviewsState;
    if (Number(currentResults) <= INITIAL_PAGE_LIMIT) {
      return 1;
    }
    return Math.ceil(
      (Number(currentResults) - INITIAL_PAGE_LIMIT) / SUBSEQUENT_PAGE_LIMIT,
    ) + 1;
  },
  /**
   * @param {ReviewsState} reviewsState
   * @returns {string[]}
   */
  [GET_REVIEWS_FREQUENTLY_MENTIONED](reviewsState) {
    return reviewsState.frequentlyMentioned;
  },
  /**
   * @param {ReviewsState} reviewState
   * @returns {string | null}
   */
  [GET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED](reviewState) {
    return reviewState.selectedFrequentlyMentioned;
  },
};

export const actions = {
  /**
   * @param {import('vuex')
   *  .ActionContext<
   *    ReviewsState,
   *    typeof import('../../store') & {
   *      pageData: PageData,
   *      envProps: { hostlink: string }
   *    }
   *  >} context
   * @param {object} options
   * @param {string} options.productId
   */
  fetchReviews(
    { commit, state: reviewsState, rootState },
    { productId },
  ) {
    commit(SET_REVIEWS_LOADING_STATE, LOADING_STATE.LOADING);
    const {
      pageData: { flags },
      envProps: { hostlink },
    } = rootState;
    const { isConsolidatedThirdVariantFlag } = flags?.groupByProducts?.[productId]?.productFlags || {};

    const isFrequentlyMentioned = reviewsState.selectedFrequentlyMentioned !== null;

    return reviewsGET({
      hostlink, productId, reviewsState, isFrequentlyMentioned,
    })
      .then(
        (response) => {
          // when using "feature" param, the response is different
          // and contains "chips" object
          const reviews = 'chips' in response.data.review
            ? response.data.review.chips.reviews
            : response.data.review.reviews;

          const currentResults = 'chips' in response.data.review
            ? response.data.review.chips.totalResult
            : response.data.review.totalResults;

          if (reviewsState.totalResults === null || (!isFrequentlyMentioned && isConsolidatedThirdVariantFlag)) {
            commit(SET_REVIEWS_TOTAL_RESULTS, currentResults || 0);
          }
          if (isConsolidatedThirdVariantFlag && !isFrequentlyMentioned) {
            if (productId
              && response.data.review?.includes?.products?.[productId]?.reviewStatistics) {
              const statistics = response.data.review.includes.products[productId].reviewStatistics;
              commit(SET_REVIEWS_STATISTICS, parseStatisticsData(statistics));
            } else {
              commit(SET_REVIEWS_STATISTICS, null);
            }
          }
          commit(SET_REVIEWS, reviews);
          commit(SET_REVIEWS_CURRENT_RESULTS, currentResults);
          commit(SET_REVIEWS_LOADING_STATE, LOADING_STATE.LOADED);
          commit(UPDATE_HEADER);
          commit(UPDATE_IS_FILTERED);
        },
      )
      .catch(
        /**
         * @param {Error} error
         */
        (error) => {
          console.error('Could not fetch reviews: ', error); // eslint-disable-line
          commit(SET_REVIEWS_LOADING_STATE, LOADING_STATE.ERROR);
        },
      );
  },
  /**
   * @param {import('vuex')
   *   .ActionContext<
   *     ReviewsState,
   *     typeof import('../../store')
   *    >} context
   * * * @param {string} productId
   */
  [FETCH_REVIEWS_PHOTOS]({ rootState, commit }, productId) {
    const {
      envProps: { hostlink },
    } = rootState;

    return reviewsPhotosGET({ hostlink, productId })
      .then(
        (response) => {
          const photos = response.data.review.reviewsWithPhoto
            .reduce(
              (acc, review) => {
                const currPhotos = review.photos.map((x) => ({
                  url: x.sizes.normal.url,
                  caption: x.caption,
                }));
                return [...acc, ...currPhotos];
              }, [],
            );
          commit(SET_REVIEWS_PHOTOS, photos);
        },
      )
      .catch(() => {
        console.error('Couldn\'t fetch reviews with photos'); // eslint-disable-line
      });
  },
  /**
   * @param {import('vuex')
   *   .ActionContext<
   *     ReviewsState,
   *     typeof import('../../store')
   *    >} context
   * @param {number} pageNumber
   */
  handlePageChange({ commit }, pageNumber) {
    commit(SET_REVIEWS_PAGE_NUMBER, pageNumber);
  },
  /**
   * @param {import('vuex')
   *   .ActionContext<
   *     ReviewsState,
   *     typeof import('../../store')
   *   >} context
   * @param {number | 'ALL_STAR_RATINGS'} starRating
   */
  handleStarRatingChange({ commit, state: reviewsState }, starRating) {
    commit(SET_REVIEWS_PAGE_NUMBER, 1);
    if (
      [SORT_BY_OPTION.LOWEST_RATING, SORT_BY_OPTION.HIGHEST_RATING].includes(
        reviewsState.sortBy,
      )
    ) {
      commit(SET_REVIEWS_SORT_BY, SORT_BY_OPTION.NEWEST);
    }
    commit(SET_REVIEWS_STAR_FILTERING, starRating);
  },
  /**
   * @param {import('vuex')
   *   .ActionContext<
   *     ReviewsState,
   *     typeof import('../../store')
   *   >} context
   * @param {string} sortBy
   */
  handleSortByChange({ commit }, sortBy) {
    commit(SET_REVIEWS_PAGE_NUMBER, 1);
    if (
      [SORT_BY_OPTION.LOWEST_RATING, SORT_BY_OPTION.HIGHEST_RATING].includes(
        sortBy,
      )
    ) {
      commit(SET_REVIEWS_STAR_FILTERING, ALL_STAR_RATINGS);
    }
    commit(SET_REVIEWS_SORT_BY, sortBy);
  },
  /**
   * @param {import('vuex')
   *   .ActionContext<
   *     ReviewsState,
   *     typeof import('../../store')
   *   >} context
   * @param {string | null} selectedFrequentlyMentioned
   */
  handleSelectedFrequentlyMentionedChange({ commit, dispatch }, selectedFrequentlyMentioned) {
    dispatch(RESET_FILTERS);
    commit(SET_REVIEWS_SORT_BY, SORT_BY_OPTION.NEWEST);
    commit(SET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED, selectedFrequentlyMentioned || null);
  },
  /**
   * @param {import('vuex')
   *   .ActionContext<
   *     ReviewsState,
   *     typeof import('../../store')
   *   >} context
   * @param {string} searchQuery
  */
  handleSearchQueryChange({ commit }, searchQuery) {
    commit(SET_REVIEWS_PAGE_NUMBER, 1);
    commit(SET_REVIEWS_SEARCH_QUERY, searchQuery);
    commit(SET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED, null);
  },
  /**
   * @param {import('vuex')
   *  .ActionContext<
   *    ReviewsState,
   *    typeof import('../../store') & {
   *      pageData: PageData,
   *      envProps: { hostlink: string }
   *    }
   *  >} context
   * * * @param {string} productId
   */
  fetchFrequentlyMentioned({ commit, rootState }, productId) {
    const {
      envProps: { hostlink },
    } = rootState;

    return reviewsFrequentlyMentionedGET({ hostlink, productId })
      .then(
        (response) => {
          const frequentlyMentioned = response.data.review.features?.FrequentlyMentioned.map(
            ({ feature }) => feature,
          );
          commit(SET_REVIEWS_FREQUENTLY_MENTIONED, frequentlyMentioned);
        },
      )
      .catch(
      /**
       * @param {Error} error
       */
        (error) => {
          console.error('Couldn\'t fetch frequently mentioned:', error); // eslint-disable-line
        },
      );
  },
  /**
   * @param {import('vuex')
   *  .ActionContext<
   *    ReviewsState,
   *    typeof import('../../store') & {
   *      pageData: PageData,
   *    }
   *  >} context
   */
  [RESET_FILTERS]({ commit }) {
    commit(SET_REVIEWS_PAGE_NUMBER, 1);
    commit(SET_REVIEWS_SEARCH_QUERY, '');
    commit(SET_REVIEWS_STAR_FILTERING, ALL_STAR_RATINGS);
    commit(SET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED, null);
  },
};
